// app.js
import React, { useState } from 'react';
import './app.css';  // Changed from './App.css' to './app.css'
import Header from './components/Header';
import PlayerSetup from './components/PlayerSetup';
import GameInterface from './components/GameInterface';
import MLPTeamSetup from './components/MLPTeamSetup';
import MLPGameInterface from './components/MLPGameInterface';
import ChampionshipFinalInterface from './components/ChampionshipFinalInterface';
import SmartBenchManager from './services/SmartBenchManager';

function App() {
  const [appState, setAppState] = useState('setup'); // 'setup', 'mlp-setup', 'playing', 'mlp-playing', 'results', 'championship'
  const [sessionType, setSessionType] = useState('smart'); // 'smart' or 'mlp'
  const [players, setPlayers] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [tournamentResults, setTournamentResults] = useState(null);
  const [showChampionshipFinal, setShowChampionshipFinal] = useState(false);
  const [rotationManager] = useState(() => new SmartBenchManager());

  // Handle session type change
  const handleSessionTypeChange = (newType) => {
    setSessionType(newType);
    // Reset state when switching session types
    setAppState('setup');
    setTeamData(null);
    setSessionConfig(null);
  };

  // Handle player import/management - FIXED
  const handleImportPlayers = (importedPlayers) => {
    console.log('Importing players:', importedPlayers);
    setPlayers(importedPlayers);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Implement file upload logic for CSV/Excel
      console.log('File upload:', file.name);
    }
  };

  // Handle session start from PlayerSetup
  const handleStartSession = (sessionPlayers, config) => {
    if (sessionType === 'smart') {
      // Start SMART session
      setPlayers(sessionPlayers);
      setSessionConfig(config);
      setAppState('playing');
    } else if (sessionType === 'mlp') {
      // Go to MLP team setup
      setPlayers(sessionPlayers);
      setAppState('mlp-setup');
    }
  };

  // Handle MLP team setup completion
  const handleTeamSetup = (teams) => {
    setTeamData(teams);
    setAppState('mlp-playing');
  };

  // Handle MLP back to player setup
  const handleBackToSetup = () => {
    setAppState('setup');
    setTeamData(null);
  };

  // Handle session completion
  const handleSessionComplete = (results) => {
    console.log('Session completed:', results);
    setTournamentResults(results);
    setAppState('results');
  };

  // Handle championship final start
  const handleStartChampionshipFinal = () => {
    setShowChampionshipFinal(true);
    setAppState('championship');
  };

  // Handle championship final completion
  const handleChampionshipComplete = (finalResults) => {
    console.log('Championship completed:', finalResults);
    // Could store final results or show final final screen
    handleReset(); // Reset after championship
  };

  // Handle app reset
  const handleReset = () => {
    setAppState('setup');
    setSessionType('smart');
    setPlayers([]);
    setTeamData(null);
    setSessionConfig(null);
    setTournamentResults(null);
    setShowChampionshipFinal(false);
    
    // Reset rotation manager
    if (rotationManager.reset) {
      rotationManager.reset();
    }
  };

  // Render based on current app state
  const renderContent = () => {
    switch (appState) {
      case 'setup':
        return (
          <PlayerSetup
            players={players}
            sessionType={sessionType}
            onSessionTypeChange={handleSessionTypeChange}
            onImportPlayers={handleImportPlayers}
            onFileUpload={handleFileUpload}
            onStartSession={handleStartSession}
          />
        );

      case 'mlp-setup':
        return (
          <MLPTeamSetup
            onTeamSetup={handleTeamSetup}
            onBack={handleBackToSetup}
          />
        );

      case 'playing':
        return (
          <GameInterface
            players={players}
            sessionConfig={sessionConfig}
            rotationManager={rotationManager}
            onSessionComplete={handleSessionComplete}
          />
        );

      case 'mlp-playing':
        return (
          <MLPGameInterface
            teamData={teamData}
            onSessionComplete={handleSessionComplete}
          />
        );

      case 'results':
        return (
          <div className="results-screen">
            <div className="results-content">
              <h2>Tournament Complete!</h2>
              {tournamentResults && tournamentResults.standings ? (
                <div className="tournament-standings">
                  <h3>Final Standings:</h3>
                  <div className="standings-list">
                    {tournamentResults.standings.map((team, index) => (
                      <div key={team.team} className="team-result">
                        <span className="team-rank">{index + 1}.</span>
                        <span className="team-name">{team.teamName}</span>
                        <span className="team-record">{team.wins}-{team.losses}</span>
                        <span className="team-percentage">({team.winPercentage}%)</span>
                      </div>
                    ))}
                  </div>
                  
                  {tournamentResults.champion && (
                    <div className="champion-display">
                      <h3>Champion: {tournamentResults.champion.teamName}</h3>
                    </div>
                  )}
                  
                  <div className="championship-options">
                    <p>Would you like to play a Championship Final between the top 2 teams?</p>
                    <p className="championship-format">First to 21, win by 2 | Rotating pairs every 5 points</p>
                    <div className="option-buttons">
                      <button onClick={handleStartChampionshipFinal} className="championship-button">
                        Play Championship Final
                      </button>
                      <button onClick={handleReset} className="reset-button">
                        End Tournament
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="results-error">
                  <p>Tournament results could not be displayed.</p>
                  <button onClick={handleReset} className="reset-button">
                    Return to Setup
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      case 'championship':
        if (tournamentResults && tournamentResults.standings && teamData) {
          const topTwoTeams = tournamentResults.standings.slice(0, 2);
          const team1Id = topTwoTeams[0].team;
          const team2Id = topTwoTeams[1].team;
          
          return (
            <ChampionshipFinalInterface
              team1Data={{
                ...teamData[team1Id],
                name: teamData[team1Id].name
              }}
              team2Data={{
                ...teamData[team2Id], 
                name: teamData[team2Id].name
              }}
              onFinalComplete={handleChampionshipComplete}
            />
          );
        } else {
          return (
            <div className="championship-error">
              <h2>Championship Final Error</h2>
              <p>Unable to load championship data.</p>
              <button onClick={handleReset} className="reset-button">
                Return to Setup
              </button>
            </div>
          );
        }

      default:
        return (
          <div className="error-screen">
            <h2>Unknown App State</h2>
            <button onClick={handleReset}>Reset App</button>
          </div>
        );
    }
  };

  return (
    <div className="App">
      <Header 
        appState={appState}
        sessionConfig={sessionConfig}
        sessionType={sessionType}
        onReset={appState !== 'setup' ? handleReset : null}
      />
      
      <main className="app-main">
        {renderContent()}
      </main>

      <style jsx>{`
        .results-screen {
          padding: 40px 20px;
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          min-height: 100vh;
          color: white;
        }

        .results-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
        }

        .results-content h2 {
          font-size: 2.5rem;
          margin-bottom: 30px;
          color: #deff01;
        }

        .tournament-standings {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 30px;
          backdrop-filter: blur(10px);
          margin-bottom: 20px;
        }

        .tournament-standings h3 {
          font-size: 1.8rem;
          margin-bottom: 20px;
          color: #deff01;
        }

        .standings-list {
          margin-bottom: 30px;
        }

        .team-result {
          display: grid;
          grid-template-columns: 40px 1fr auto auto;
          gap: 15px;
          align-items: center;
          padding: 12px 20px;
          margin: 8px 0;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          font-size: 1.1rem;
        }

        .team-rank {
          font-weight: bold;
          color: #deff01;
        }

        .team-name {
          text-align: left;
          font-weight: bold;
        }

        .team-record, .team-percentage {
          font-family: monospace;
        }

        .champion-display {
          background: linear-gradient(135deg, #deff01 0%, #bef264 100%);
          color: #1e3a8a;
          border-radius: 15px;
          padding: 20px;
          margin: 20px 0;
        }

        .champion-display h3 {
          margin: 0;
          font-size: 1.5rem;
          color: #1e3a8a;
        }

        .championship-options p {
          font-size: 1.2rem;
          margin: 15px 0;
        }

        .championship-format {
          font-size: 1rem;
          opacity: 0.8;
          font-style: italic;
        }

        .option-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          margin-top: 30px;
        }

        .championship-button {
          background: linear-gradient(135deg, #deff01 0%, #bef264 100%);
          color: #1e3a8a;
          border: none;
          padding: 15px 30px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s;
        }

        .championship-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(222, 255, 1, 0.4);
        }

        .reset-button {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.3);
          padding: 15px 30px;
          border-radius: 12px;
          font-size: 1.1rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s;
        }

        .reset-button:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .results-error, .championship-error {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 40px;
          backdrop-filter: blur(10px);
        }

        .error-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          gap: 20px;
        }
      `}</style>
    </div>
  );
}

export default App;